<script setup lang="ts">
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import { useTRDocumentStore, TRDocumentWithRevisions } from './TRDocumentStore';
    import {
        getTRDocumentListLink,
        getTRRevisionContentEditorLink,
        getLibraryLink
    } from '@/shared/helpers/routing';
    import { ref, watch } from 'vue';
    import { shortDate } from '@/formatters';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import { TRRevisionRecord } from './TRDocumentQueries';
    import { useRouter } from 'vue-router';
    import Spinner from '@/shared/components/Spinner.vue';
    import { createNewTrDocumentRevision, getTRDocumentContentLibraries, TRDocumentContentLibrary } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import CodeEditSaveProgress from '@/shared/components/CodeEditSaveProgress.vue';
    import NewTrRevisionDialog, { NewRevisionInfo } from '@/apps/trdocument/NewTrRevisionDialog.vue';

    type Props = {
        trDocumentIdentity: string;
    };

    const props = defineProps<Props>();
    const store = useTRDocumentStore();
    const router = useRouter();
    const trDocument = ref<TRDocumentWithRevisions | undefined>();
    const contentLibraryList = ref<TRDocumentContentLibrary[]>([]);
    const newRevisionReleaseId = ref<number | null>(null);
    const showSaveProgress = ref<boolean>(false);
    const showNewRevisionDialog = ref<boolean>(false);

    watch(() => props.trDocumentIdentity, async () => {
        await store.init();
        trDocument.value = store.getTRDocument(props.trDocumentIdentity);
        contentLibraryList.value = await getTRDocumentContentLibraries(http, trDocument.value!.document.identity);
    }, { immediate: true });

    async function onEditRevisionClick(revision: TRRevisionRecord): Promise<void> {
        await router.push(getTRRevisionContentEditorLink({
            trDocumentIdentity: trDocument.value?.document.identity,
            trRevisionIdentity: revision.identity
        }));
    }

    function onNewTRRevision(): void {
        if (!trDocument.value) {
            alert('TR Document not loaded yet');
            return;
        }

        // Check if there are any existing revision that is not IsAllFinished
        const existingUnfinishedRevision = trDocument.value!.revisions.find(revision => !revision.isAllFinished);
        if (existingUnfinishedRevision) {
            alert('Cannot create a new revision while there is an existing unfinished revision: ' + existingUnfinishedRevision.identity);
            return;
        }

        showNewRevisionDialog.value = true;
    }

    async function onNewTRRevisionConfirmed(revisionInfo: NewRevisionInfo): Promise<void> {
        showSaveProgress.value = true;
        showNewRevisionDialog.value = false;

        const releaseId = await createNewTrDocumentRevision(
            http,
            trDocument.value!.document.identity,
            {
                name: revisionInfo.name,
                description: revisionInfo.description
            });
        newRevisionReleaseId.value = releaseId.data;
    }

    function onNewTRRevisionCancelled(): void {
        showSaveProgress.value = false;
        showNewRevisionDialog.value = false;
    }
    async function onNewRevisionCreated(): Promise<void> {
        showSaveProgress.value = false;
        if (!trDocument.value) {
            return;
        }
        trDocument.value.revisions = await store.getTRDocumentRevision(trDocument.value!.document.identity, true) ?? [];
        showNewRevisionDialog.value = false;
    }

    function onNewContentLibrary(): void {
        alert('Not yet implemented');
    }
</script>

<template>
    <spinner :loading="store.initializing">
        <div
            v-if="trDocument"
            class="mt-5">
            <div class="buttons is-pulled-right">
                <b-button
                    icon-left="chevron-left"
                    @click="router.push(getTRDocumentListLink())">
                    Back
                </b-button>
                <b-button
                    v-require-can-edit-code="{ libraryName: 'TRRevision' }"
                    type="is-primary"
                    icon-left="plus-circle-outline"
                    @click="onNewTRRevision()">
                    Create new revision
                </b-button>
                <b-button
                    v-require-can-edit-code="{ libraryName: 'TRDocument' }"
                    type="is-primary"
                    icon-left="plus-circle-outline"
                    @click="onNewContentLibrary()">
                    Add content library
                </b-button>
            </div>
            <h3 class="title is-4">
                {{ trDocument.document.name }} / {{ trDocument.document.technologyDescription }} / {{ trDocument.document.validityContextDescription }}
            </h3>
            <div class="trdocument-section-header block">
                Revisions
            </div>
            <b-table
                :data="trDocument.revisions"
                :loading="store.initializing"
                striped>
                <b-table-column
                    v-slot="tableProps"
                    field="name"
                    label="Revision identity"
                    sortable>
                    {{ tableProps.row.identity }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="name"
                    label="Name"
                    sortable>
                    {{ tableProps.row.name }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="description"
                    label="Description"
                    sortable>
                    {{ tableProps.row.description }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="areDataInitialized"
                    label="Data initialized"
                    sortable
                    centered>
                    <bool-element
                        :value="tableProps.row.areDataInitialized ?? false"
                        :has-yes-no="true" />
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="areDataFinished"
                    label="Data finished"
                    sortable
                    centered>
                    <bool-element
                        :value="tableProps.row.areDataFinished ?? false"
                        :has-yes-no="true" />
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="areQueriesFinished"
                    label="Queries finished"
                    sortable
                    centered>
                    <bool-element
                        :value="tableProps.row.areQueriesFinished ?? false"
                        :has-yes-no="true" />
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="areURLsFinished"
                    label="URLs finished"
                    sortable
                    centered>
                    <bool-element
                        :value="tableProps.row.areURLsFinished ?? false"
                        :has-yes-no="true" />
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="isAllFinished"
                    label="All finished"
                    sortable
                    centered>
                    <bool-element
                        :value="tableProps.row.isAllFinished ?? false"
                        :has-yes-no="true" />
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="issueDate"
                    label="Issue date"
                    sortable
                    centered>
                    {{ shortDate(tableProps.row.issueDate) }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="identity"
                    label="&nbsp;"
                    centered>
                    <b-button
                        type="is-primary"
                        icon-left="pencil"
                        outlined
                        @click="onEditRevisionClick(tableProps.row)">
                        Edit
                    </b-button>
                </b-table-column>
            </b-table>
            <div class="trdocument-section-header block">
                Content libraries
            </div>
            <b-table
                :data="contentLibraryList"
                :loading="store.initializing"
                striped>
                <b-table-column
                    v-slot="tableProps"
                    field="name"
                    label="TR content library"
                    sortable>
                    <router-link :to="getLibraryLink(tableProps.row.name)">
                        {{ tableProps.row.name }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="contentForLibrary"
                    label="Main library"
                    sortable>
                    <router-link :to="getLibraryLink(tableProps.row.contentForLibrary)">
                        {{ tableProps.row.contentForLibrary }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="name"
                    label="Combined view"
                    sortable>
                    <router-link :to="getLibraryLink('TR' + tableProps.row.contentForLibrary + 'Revision')">
                        TR{{ tableProps.row.contentForLibrary }}Revision
                    </router-link>
                </b-table-column>
            </b-table>
        </div>
    </spinner>
    <new-tr-revision-dialog
        :active="showNewRevisionDialog"
        @create="onNewTRRevisionConfirmed"
        @cancel="onNewTRRevisionCancelled" />
    <code-edit-save-progress
        :close-enabled="false"
        :release-id="newRevisionReleaseId"
        :save-process-started="showSaveProgress"
        @success="onNewRevisionCreated" />
</template>
